import { ColumnProps } from './column';
import orderBy from 'lodash-es/orderBy';

export interface TableItem {
  id: string;
}

export const idSort = (a, b) =>
  a.id.localeCompare(b.id, undefined, { numeric: true });

export const trueOrUndefined = (property: any | undefined) =>
  !!property || typeof property === 'undefined';

export const isSortable = (column: ColumnProps) => trueOrUndefined(column.sort);

export const isSearchable = (column: ColumnProps) =>
  trueOrUndefined(column.search);

export const sortByColumn = (
  columns: ColumnProps[],
  columnKey: string,
  items: TableItem[],
  sortAscending: boolean,
): TableItem[] => {
  const column = columns.find(c => c.dataKey === columnKey);

  const sortOrder = sortAscending ? 'asc' : 'desc';

  const { dataKey, sort, dataAccessor, isSortableCondition } = column;

  if (!isSortable(column)) {
    throw new Error(`Column ${dataKey} is not sortable`);
  }

  let sortableItems = [];
  const unsortableItems = [];

  // organize items into sortable and unsortable
  if (isSortableCondition) {
    items.forEach(item =>
      !!isSortableCondition(item)
        ? sortableItems.push(item)
        : unsortableItems.push(item),
    );
  } else {
    sortableItems = items;
  }

  // custom sort using a non-primitive function
  if (typeof sort === 'function') {
    const multiplier = sortAscending ? -1 : 1;
    const functionSortIDFallback = (A, B) =>
      sort(A, B, sortAscending) || idSort(A, B) * multiplier;

    sortableItems.sort(functionSortIDFallback);
  } else {
    // auto sort using an accessor function
    const iteratee = dataAccessor || dataKey;
    sortableItems = orderBy(sortableItems, iteratee, sortOrder);
  }

  unsortableItems.sort(idSort);

  return [...sortableItems, ...unsortableItems];
};

export const searchMatchItems = (
  columns: ColumnProps[],
  items: TableItem[],
  search: string,
): TableItem[] => {
  if (!search) {
    return items;
  }

  const mash = item =>
    columns
      .filter(isSearchable)
      .map(column =>
        column.dataAccessor ? column.dataAccessor(item) : item[column.dataKey],
      )
      .join('|')
      .toLowerCase();

  const searchLowercase = search.toLowerCase();
  return items.filter(item => mash(item).includes(searchLowercase));
};

export const noop = () => undefined;
